import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useCreateLead, useLeadStatuses } from "../hooks/useLeads";
import type { LeadDetail } from "../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (lead: LeadDetail) => void;
};

export function NewLeadDrawer({ isOpen, onClose, onCreated }: Props) {
  const create = useCreateLead();
  const { data: statuses = [] } = useLeadStatuses();
  const users = useUserList({ limit: 100, status: "ACTIVE" });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState("new");
  const [ownerId, setOwnerId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName("");
    setEmail("");
    setPhone("");
    setCompanyName("");
    setJobTitle("");
    setCountry("");
    setStatus("new");
    setOwnerId("");
    setNotes("");
    setError(null);
  }

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // Need at least one identifying signal — name, email, or phone.
    if (!name.trim() && !email.trim() && !phone.trim()) {
      setError("Provide at least a name, email, or phone.");
      return;
    }
    try {
      const res = await create.mutateAsync({
        name: name.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        companyName: companyName.trim() || null,
        jobTitle: jobTitle.trim() || null,
        country: country.trim() || null,
        status,
        ownerId: ownerId || null,
        notes: notes.trim() || null,
      });
      onCreated?.(res.lead);
      onClose();
    } catch (e) {
      setError((e as Error)?.message ?? "Create failed");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex">
      <div
        className="flex-1 bg-black/30"
        onClick={() => !create.isPending && onClose()}
      />
      <div className="flex h-full w-full max-w-[480px] flex-col bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-foreground">New Lead</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={create.isPending}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 space-y-4 overflow-y-auto px-5 py-4"
        >
          <p className="text-xs text-muted-foreground">
            Manually create a Lead — typical for business cards collected at
            events, phone leads, or partners introduced by a colleague. Inbound
            enquiries auto-create Leads via the public form / email API.
          </p>

          <div className="space-y-1.5">
            <Label>Full Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Company</Label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Job Title</Label>
              <Input
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {statuses.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Owner</Label>
              <Select
                value={ownerId}
                onChange={(e) => setOwnerId(e.target.value)}
              >
                <option value="">— Unassigned —</option>
                {users.data?.data.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              style={{ borderColor: "hsl(210,30%,85%)" }}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </form>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={create.isPending}
          >
            {create.isPending && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
            )}
            Create Lead
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
